/**
 * TierManager — inline widget for managing country tiers.
 *
 * Features:
 *   - List existing tiers as compact rows
 *   - Create / edit tiers with name + country multi-select
 *   - Load countries from a Google Ads campaign's geo targeting
 *   - Delete tiers with confirmation
 *   - Search/filter countries in the multi-select
 *
 * Uses @tanstack/react-query for data fetching and mutation.
 */
import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Layers, Plus, Pencil, Trash2, X, Check, ChevronDown, ChevronRight, Search, Upload,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { api } from '@/services/api';
import { endpoints } from '@/services/endpoints';
import { Button } from '@/components/ui/Button';
import { toast } from '@/components/ui/Toast';
import { collapse } from '@/design/animations';

// ─── Types ───────────────────────────────────────────────────────────────────

interface Tier {
  id: string;
  name: string;
  countryCodes: string[];
  platform?: string; // 'android' | 'ios' | undefined (all)
  createdAt: string;
  updatedAt: string;
}

interface Country {
  countryCode: string;
  countryName: string;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function TierManager() {
  const queryClient = useQueryClient();
  const [editingTier, setEditingTier] = useState<Tier | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  // ─── Queries ─────────────────────────────────────────────────────────────

  const { data: tiers = [], isLoading: tiersLoading } = useQuery<Tier[]>({
    queryKey: ['marketing', 'tiers'],
    queryFn: () => api.get<Tier[]>(endpoints.marketing.tiers.list()),
  });

  const { data: countries = [] } = useQuery<Country[]>({
    queryKey: ['marketing', 'countries'],
    queryFn: () => api.get<Country[]>(endpoints.marketing.countries()),
    staleTime: 3600_000, // Cache for 1 hour
  });

  const { data: campaignNames = [] } = useQuery<string[]>({
    queryKey: ['marketing', 'campaigns'],
    queryFn: () => api.get<string[]>(endpoints.marketing.campaigns()),
    staleTime: 300_000,
  });

  // ─── Mutations ───────────────────────────────────────────────────────────

  const createMutation = useMutation({
    mutationFn: (input: { name: string; countryCodes: string[]; platform?: string }) =>
      api.post<Tier>(endpoints.marketing.tiers.create(), input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketing', 'tiers'] });
      setIsCreating(false);
      toast.success('Tier created');
    },
    onError: () => toast.error('Failed to create tier'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...input }: { id: string; name: string; countryCodes: string[]; platform?: string | null }) =>
      api.patch<Tier>(endpoints.marketing.tiers.update(id), input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketing', 'tiers'] });
      setEditingTier(null);
      toast.success('Tier updated');
    },
    onError: () => toast.error('Failed to update tier'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      api.delete(endpoints.marketing.tiers.delete(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketing', 'tiers'] });
      setDeleteConfirm(null);
      toast.success('Tier deleted');
    },
    onError: () => toast.error('Failed to delete tier'),
  });

  // ─── Country name lookup ─────────────────────────────────────────────────

  const codeToName = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of countries) map.set(c.countryCode, c.countryName);
    return map;
  }, [countries]);

  const displayCountries = useCallback(
    (codes: string[]) => {
      if (codes.length === 0) return 'No countries';
      const names = codes.map((c) => codeToName.get(c) || c);
      if (names.length <= 4) return names.join(', ');
      return `${names.slice(0, 3).join(', ')} +${names.length - 3} more`;
    },
    [codeToName],
  );

  // ─── Render ──────────────────────────────────────────────────────────────

  const [expanded, setExpanded] = useState(false);
  const hasContent = tiers.length > 0 || isCreating;

  return (
    <div>
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 transition-colors"
        >
          {expanded ? (
            <ChevronDown className="w-3 h-3 text-slate-400" />
          ) : (
            <ChevronRight className="w-3 h-3 text-slate-400" />
          )}
          <Layers className="w-3.5 h-3.5" />
          Tiers
          {tiers.length > 0 && (
            <span className="bg-brand-50 text-brand-600 text-2xs px-1.5 py-0.5 rounded-full font-semibold">
              {tiers.length}
            </span>
          )}
        </button>
        <Button
          variant="ghost"
          size="sm"
          icon={<Plus className="w-3.5 h-3.5" />}
          onClick={() => {
            setIsCreating(true);
            setEditingTier(null);
            setExpanded(true);
          }}
          disabled={isCreating}
        >
          Add Tier
        </Button>
      </div>

      <AnimatePresence>
        {expanded && hasContent && (
          <motion.div
            variants={collapse}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="mt-2"
          >
            {tiersLoading && (
              <p className="text-xs text-slate-400">Loading tiers...</p>
            )}

            {/* Existing tiers list */}
            {tiers.length > 0 && (
              <div className="space-y-1.5 mb-2">
                {tiers.map((tier) => (
                  <div key={tier.id}>
                    {editingTier?.id === tier.id ? (
                      <TierForm
                        initialName={tier.name}
                        initialCodes={tier.countryCodes}
                        initialPlatform={tier.platform}
                        countries={countries}
                        campaignNames={campaignNames}
                        codeToName={codeToName}
                        loading={updateMutation.isPending}
                        onSave={(name, codes, platform) =>
                          updateMutation.mutate({ id: tier.id, name, countryCodes: codes, platform })
                        }
                        onCancel={() => setEditingTier(null)}
                      />
                    ) : deleteConfirm === tier.id ? (
                      <div className="flex items-center gap-2 p-2 rounded-lg bg-red-50 border border-red-200">
                        <span className="text-xs text-red-700 flex-1">
                          Delete &quot;{tier.name}&quot;?
                        </span>
                        <button
                          onClick={() => deleteMutation.mutate(tier.id)}
                          disabled={deleteMutation.isPending}
                          className="text-xs text-red-600 hover:text-red-800 font-medium"
                        >
                          {deleteMutation.isPending ? 'Deleting...' : 'Yes, Delete'}
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(null)}
                          className="text-xs text-slate-500 hover:text-slate-700 font-medium"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 group p-2 rounded-lg hover:bg-slate-50 transition-colors">
                        <div className="flex-1 min-w-0">
                          <span className="text-xs font-semibold text-slate-800">{tier.name}</span>
                          {tier.platform && (
                            <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-2xs font-medium bg-violet-50 text-violet-600 border border-violet-200">
                              {tier.platform === 'android' ? 'Android' : 'iOS'}
                            </span>
                          )}
                          <span className="text-2xs text-slate-400 ml-2">
                            {displayCountries(tier.countryCodes)}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => {
                              setEditingTier(tier);
                              setIsCreating(false);
                            }}
                            className="w-6 h-6 rounded-md flex items-center justify-center text-slate-400 hover:text-brand-600 hover:bg-brand-50 transition-colors"
                            title="Edit"
                          >
                            <Pencil className="w-3 h-3" />
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(tier.id)}
                            className="w-6 h-6 rounded-md flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Create form */}
            {isCreating && (
              <TierForm
                countries={countries}
                campaignNames={campaignNames}
                codeToName={codeToName}
                loading={createMutation.isPending}
                onSave={(name, codes, platform) =>
                  createMutation.mutate({ name, countryCodes: codes, ...(platform ? { platform } : {}) })
                }
                onCancel={() => setIsCreating(false)}
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Tier Form (create/edit) ─────────────────────────────────────────────────

interface TierFormProps {
  initialName?: string;
  initialCodes?: string[];
  initialPlatform?: string;
  countries: Country[];
  campaignNames: string[];
  codeToName: Map<string, string>;
  loading: boolean;
  onSave: (name: string, countryCodes: string[], platform?: string | null) => void;
  onCancel: () => void;
}

const PLATFORM_OPTIONS = [
  { value: '', label: 'All Platforms' },
  { value: 'android', label: 'Android' },
  { value: 'ios', label: 'iOS' },
];

function TierForm({
  initialName = '',
  initialCodes = [],
  initialPlatform = '',
  countries,
  campaignNames,
  codeToName,
  loading,
  onSave,
  onCancel,
}: TierFormProps) {
  const [name, setName] = useState(initialName);
  const [selectedCodes, setSelectedCodes] = useState<Set<string>>(new Set(initialCodes));
  const [platform, setPlatform] = useState(initialPlatform || '');
  const [search, setSearch] = useState('');
  const [showCountryPicker, setShowCountryPicker] = useState(false);
  const [showCampaignDropdown, setShowCampaignDropdown] = useState(false);
  const [loadingCampaign, setLoadingCampaign] = useState(false);

  // Refs for trigger buttons (used to calculate portal position)
  const pickerTriggerRef = useRef<HTMLButtonElement>(null);
  const campaignTriggerRef = useRef<HTMLButtonElement>(null);

  // Refs for the portaled dropdowns (used for outside-click detection)
  const pickerDropdownRef = useRef<HTMLDivElement>(null);
  const campaignDropdownRef = useRef<HTMLDivElement>(null);

  // Position state for portaled dropdowns
  const [pickerPos, setPickerPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const [campaignPos, setCampaignPos] = useState<{ top: number; right: number } | null>(null);

  // Calculate picker dropdown position
  const updatePickerPos = useCallback(() => {
    if (!pickerTriggerRef.current) return;
    const rect = pickerTriggerRef.current.getBoundingClientRect();
    // Place dropdown above if it would overflow the viewport bottom
    const dropdownHeight = 280; // estimated: search + actions + list
    const spaceBelow = window.innerHeight - rect.bottom;
    const top = spaceBelow < dropdownHeight
      ? Math.max(8, rect.top - dropdownHeight - 4)
      : rect.bottom + 4;
    setPickerPos({ top, left: rect.left, width: rect.width });
  }, []);

  const updateCampaignPos = useCallback(() => {
    if (!campaignTriggerRef.current) return;
    const rect = campaignTriggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const dropdownHeight = 200;
    const top = spaceBelow < dropdownHeight
      ? Math.max(8, rect.top - dropdownHeight - 4)
      : rect.bottom + 4;
    setCampaignPos({ top, right: window.innerWidth - rect.right });
  }, []);

  // Open/close country picker
  const toggleCountryPicker = useCallback(() => {
    setShowCountryPicker((prev) => {
      if (!prev) updatePickerPos();
      return !prev;
    });
    setShowCampaignDropdown(false);
  }, [updatePickerPos]);

  // Open/close campaign dropdown
  const toggleCampaignDropdown = useCallback(() => {
    setShowCampaignDropdown((prev) => {
      if (!prev) updateCampaignPos();
      return !prev;
    });
    setShowCountryPicker(false);
  }, [updateCampaignPos]);

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      const target = e.target as Node;
      if (
        showCountryPicker &&
        pickerDropdownRef.current && !pickerDropdownRef.current.contains(target) &&
        pickerTriggerRef.current && !pickerTriggerRef.current.contains(target)
      ) {
        setShowCountryPicker(false);
      }
      if (
        showCampaignDropdown &&
        campaignDropdownRef.current && !campaignDropdownRef.current.contains(target) &&
        campaignTriggerRef.current && !campaignTriggerRef.current.contains(target)
      ) {
        setShowCampaignDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showCountryPicker, showCampaignDropdown]);

  // Reposition on scroll/resize while open
  useEffect(() => {
    if (!showCountryPicker) return;
    const handler = () => updatePickerPos();
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
    };
  }, [showCountryPicker, updatePickerPos]);

  useEffect(() => {
    if (!showCampaignDropdown) return;
    const handler = () => updateCampaignPos();
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
    };
  }, [showCampaignDropdown, updateCampaignPos]);

  const filteredCountries = useMemo(() => {
    if (!search) return countries;
    const q = search.toLowerCase();
    return countries.filter(
      (c) =>
        c.countryName.toLowerCase().includes(q) ||
        c.countryCode.toLowerCase().includes(q),
    );
  }, [countries, search]);

  const toggleCode = (code: string) => {
    setSelectedCodes((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const loadFromCampaign = async (campaignName: string) => {
    setLoadingCampaign(true);
    setShowCampaignDropdown(false);
    try {
      const result = await api.get<{ campaignName: string; countryCodes: string[] }>(
        endpoints.marketing.campaignCountries(campaignName),
      );
      if (result.countryCodes.length > 0) {
        setSelectedCodes((prev) => {
          const next = new Set(prev);
          for (const code of result.countryCodes) next.add(code);
          return next;
        });
        toast.success(`Loaded ${result.countryCodes.length} countries from "${campaignName}"`);
      } else {
        toast.info(`No geo targeting found for "${campaignName}"`);
      }
    } catch {
      toast.error('Failed to load campaign countries');
    } finally {
      setLoadingCampaign(false);
    }
  };

  const handleSave = () => {
    if (!name.trim()) return;
    onSave(name.trim(), Array.from(selectedCodes).sort(), platform || null);
  };

  const codesArray = Array.from(selectedCodes).sort();

  return (
    <div className="p-3 rounded-lg border border-slate-200 bg-white space-y-3">
      {/* Name + Platform row */}
      <div className="flex items-end gap-3">
        <div className="flex-1">
          <label className="text-2xs font-medium text-slate-500 mb-1 block">Tier Name</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Tier 1"
            className="w-full h-8 px-2.5 rounded-md border border-slate-200 bg-white text-xs focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400"
            autoFocus
          />
        </div>
        <div className="w-36">
          <label className="text-2xs font-medium text-slate-500 mb-1 block">Platform</label>
          <select
            value={platform}
            onChange={(e) => setPlatform(e.target.value)}
            className="w-full h-8 px-2 rounded-md border border-slate-200 bg-white text-xs focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400"
          >
            {PLATFORM_OPTIONS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Selected countries */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-2xs font-medium text-slate-500">
            Countries ({selectedCodes.size})
          </label>
          <div className="flex items-center gap-1.5">
            {/* Load from campaign button */}
            <button
              ref={campaignTriggerRef}
              type="button"
              onClick={toggleCampaignDropdown}
              disabled={loadingCampaign}
              className="flex items-center gap-1 text-2xs text-brand-600 hover:text-brand-800 font-medium"
            >
              <Upload className="w-3 h-3" />
              {loadingCampaign ? 'Loading...' : 'Load from Campaign'}
            </button>

            {/* Campaign dropdown — portaled */}
            {showCampaignDropdown && campaignPos && createPortal(
              <div
                ref={campaignDropdownRef}
                className="fixed z-[9999] w-64 max-h-48 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg py-1"
                style={{ top: campaignPos.top, right: campaignPos.right }}
              >
                {campaignNames.length === 0 ? (
                  <p className="px-3 py-2 text-xs text-slate-400">No campaigns found</p>
                ) : (
                  campaignNames.map((cName) => (
                    <button
                      key={cName}
                      type="button"
                      onClick={() => loadFromCampaign(cName)}
                      className="w-full text-left px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50 truncate"
                    >
                      {cName}
                    </button>
                  ))
                )}
              </div>,
              document.body,
            )}
          </div>
        </div>

        {/* Country picker trigger */}
        <button
          ref={pickerTriggerRef}
          type="button"
          onClick={toggleCountryPicker}
          className={cn(
            'w-full h-8 px-2.5 rounded-md border text-left text-xs flex items-center justify-between',
            'transition-colors',
            showCountryPicker
              ? 'border-brand-400 ring-2 ring-brand-500/20'
              : 'border-slate-200 hover:border-slate-300',
          )}
        >
          <span className="text-slate-500">Select countries...</span>
          <ChevronDown className={cn(
            'w-3.5 h-3.5 text-slate-400 transition-transform',
            showCountryPicker && 'rotate-180',
          )} />
        </button>

        {/* Country picker dropdown — portaled to prevent clipping */}
        {showCountryPicker && pickerPos && createPortal(
          <div
            ref={pickerDropdownRef}
            className="fixed z-[9999] bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden"
            style={{ top: pickerPos.top, left: pickerPos.left, width: pickerPos.width }}
          >
            {/* Search */}
            <div className="p-2 border-b border-slate-100">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search countries..."
                  className="w-full h-7 pl-7 pr-2 rounded-md border border-slate-200 bg-white text-xs focus:outline-none focus:ring-1 focus:ring-brand-500/20"
                  autoFocus
                />
              </div>
            </div>

            {/* Quick actions */}
            <div className="px-2 py-1.5 border-b border-slate-100 flex gap-2">
              <button
                type="button"
                onClick={() => {
                  const allCodes = filteredCountries.map((c) => c.countryCode);
                  setSelectedCodes((prev) => {
                    const next = new Set(prev);
                    allCodes.forEach((c) => next.add(c));
                    return next;
                  });
                }}
                className="text-2xs text-brand-600 hover:text-brand-800 font-medium"
              >
                Select All{search ? ' Filtered' : ''}
              </button>
              <span className="text-2xs text-slate-300">|</span>
              <button
                type="button"
                onClick={() => {
                  if (search) {
                    const filterCodes = new Set(filteredCountries.map((c) => c.countryCode));
                    setSelectedCodes((prev) => {
                      const next = new Set(prev);
                      filterCodes.forEach((c) => next.delete(c));
                      return next;
                    });
                  } else {
                    setSelectedCodes(new Set());
                  }
                }}
                className="text-2xs text-slate-500 hover:text-slate-700 font-medium"
              >
                Deselect All{search ? ' Filtered' : ''}
              </button>
            </div>

            {/* Country list */}
            <div className="max-h-48 overflow-y-auto">
              {filteredCountries.length === 0 ? (
                <p className="px-3 py-2 text-xs text-slate-400">No countries match your search</p>
              ) : (
                filteredCountries.map((c) => {
                  const isSelected = selectedCodes.has(c.countryCode);
                  return (
                    <button
                      key={c.countryCode}
                      type="button"
                      onClick={() => toggleCode(c.countryCode)}
                      className={cn(
                        'w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left transition-colors',
                        isSelected ? 'bg-brand-50 text-brand-700' : 'text-slate-700 hover:bg-slate-50',
                      )}
                    >
                      <div className={cn(
                        'w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0',
                        isSelected
                          ? 'bg-brand-500 border-brand-500'
                          : 'border-slate-300',
                      )}>
                        {isSelected && <Check className="w-2.5 h-2.5 text-white" />}
                      </div>
                      <span className="flex-1 truncate">{c.countryName}</span>
                      <span className="text-2xs text-slate-400 shrink-0">{c.countryCode}</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>,
          document.body,
        )}

        {/* Country tags — below the picker */}
        {codesArray.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {codesArray.map((code) => (
              <span
                key={code}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-brand-50 text-brand-700 text-2xs font-medium"
              >
                {codeToName.get(code) || code}
                <button
                  type="button"
                  onClick={() => toggleCode(code)}
                  className="hover:text-red-500 transition-colors"
                >
                  <X className="w-2.5 h-2.5" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={handleSave}
          loading={loading}
          disabled={!name.trim() || selectedCodes.size === 0}
        >
          {initialName ? 'Update' : 'Create'} Tier
        </Button>
      </div>
    </div>
  );
}
